import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
  getDisplayYear,
  getFutureLiveStartTimestamp,
  isLiveContent,
} from "../../utils/liveStartCountdown";
import {
  focusHomeMenu,
  resetRowTransformAfterBlur,
  resetRowTransformIfUnfocused,
  syncFocusedRowTransform,
} from "./focusHelpers";

const ContentItem = ({ item = {}, onFocus, onBlur, isFirst, focusKey }) => {
  const navigate = useNavigate();
  const { slug, contentPhotos, title, year, tags, kind, contentType } = item;
  const liveStartTimestamp = isLiveContent(item)
    ? getFutureLiveStartTimestamp(item)
    : null;
  const displayYear = getDisplayYear(year);
  const navigationUrl = `/${contentType?.kind || kind || "movie"}/${slug}`;

  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    onFocus,
    onBlur,
    onEnterPress: () => {
      navigate(navigationUrl);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        return focusHomeMenu();
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData =
    getOptimizedImageForType("horizontalSportCover", contentPhotos, "TV", {
      quality: 90,
      format: "jpeg",
    }) ||
    getOptimizedImageForType("horizontalCover", contentPhotos, "TV", {
      quality: 90,
      format: "jpeg",
    });

  return (
    <div className="legacy-sport-card-wrapper">
      <div
        ref={ref}
        className={"legacy-sport-card" + (focused ? " is-focused" : "")}
        onClick={() => navigate(navigationUrl)}
        onMouseEnter={handleMouseEnter}
      >
        <OptimizedImage
          src={imageData.src}
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: "cover",
            borderRadius: "20px",
            position: "absolute",
          }}
        />
        <div className="legacy-tv-card-focus-border" />
        <div className="legacy-tv-card-overlay">
          <span className="legacy-tv-card-title">{title}</span>
          {displayYear && (
            <span className="legacy-tv-card-year">{displayYear}</span>
          )}
        </div>
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
      </div>
      {liveStartTimestamp && (
        <LiveStartCountdown
          startTimestamp={liveStartTimestamp}
          size="sportCard"
          style={{ marginTop: 8, justifyContent: "center" }}
        />
      )}
    </div>
  );
};

export const Topabakh = ({ movies, onFocus, rowTitle, id, index }) => {
  const itemFocusKeyPrefix = "topabakh_item_" + id + "_";
  const { ref, focusKey } = useFocusable({
    focusKey: "topabakh_" + id,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusable: false,
    trackChildren: true,
    autoRestoreFocus: true,
    preferredChildFocusKey: itemFocusKeyPrefix + "0",
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  useLayoutEffect(() => {
    var delays = [0, 80, 220, 600, 1400, 2600];
    resetRowTransformIfUnfocused(innerRef);
    var timers = delays.map(function (delay) {
      return setTimeout(function () {
        resetRowTransformIfUnfocused(innerRef);
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [movies.length]);
  const { isMouseFocus } = useInputMode();
  const onAssetFocus = useCallback(
    (props) => {
      if (typeof onFocus === "function") onFocus(props);
      if (isMouseFocus) return;
      syncFocusedRowTransform({
        innerRef,
        scrollerRef: scrollingRef,
        leftPadding: 360,
      });
    },
    [isMouseFocus, onFocus],
  );
  const onAssetBlur = useCallback(
    () => {
      resetRowTransformAfterBlur(innerRef);
    },
    []
  );

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-sport-row" ref={ref} tabIndex={index}>
        <h2 className="legacy-sport-row-title">{rowTitle}</h2>
        <div className="legacy-sport-scroller" ref={scrollingRef}>
          <div className="legacy-sport-content" ref={innerRef}>
            {movies?.map((item, idx) => {
              return (
                <ContentItem
                  item={item.content}
                  key={"tp_" + item.content.slug}
                  onFocus={onAssetFocus}
                  onBlur={onAssetBlur}
                  isFirst={idx === 0}
                  focusKey={itemFocusKeyPrefix + idx}
                />
              );
            })}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
